import React, { CSSProperties } from "react";

interface ContactInfoExportProps {
  clinicName?: string;
  address?: string;
  phone?: string;
  email?: string;
  workingHours?: string;
  mapEmbedUrl?: string;
  showMap?: boolean;
  backgroundColor?: string;
  textColor?: string;
  layout?: "vertical" | "horizontal" | "with-map";
  maxWidth?: number;
  paddingX?: number;
  paddingTop?: number;
  paddingBottom?: number;
}

export const ContactInfoExport: React.FC<ContactInfoExportProps> = ({
  clinicName = "Premium Clinic",
  address = "123 Medical Center Road, Bangkok 10110",
  phone = "02-123-4567",
  email = "contact@clinic.com",
  workingHours: workingHoursProp,
  mapEmbedUrl,
  showMap = false,
  backgroundColor = "#f9fafb",
  textColor = "#1f2937",
  layout = "vertical",
  maxWidth = 0,
  paddingX = 24,
  paddingTop = 24,
  paddingBottom = 24,
}) => {
  // Handle legacy data where workingHours might be an array
  const workingHours = Array.isArray(workingHoursProp) ? workingHoursProp.join("\n") : workingHoursProp;
  const isHorizontal = layout === "horizontal";
  const isWithMap = layout === "with-map";

  const containerStyle: CSSProperties = {
    backgroundColor,
    paddingTop: `${paddingTop}px`,
    paddingBottom: `${paddingBottom}px`,
    paddingLeft: `${paddingX}px`,
    paddingRight: `${paddingX}px`,
    borderRadius: "8px",
    ...(maxWidth > 0 ? { maxWidth: `${maxWidth}px`, marginLeft: "auto", marginRight: "auto" } : {}),
  };

  const contactItemsStyle: CSSProperties = {
    display: "flex",
    flexDirection: isHorizontal ? "row" : "column",
    flexWrap: isHorizontal ? "wrap" : "nowrap",
    gap: isHorizontal ? "32px" : "16px",
  };

  const itemBoxStyle: CSSProperties = {
    flex: isHorizontal ? 1 : "none",
    minWidth: isHorizontal ? "200px" : "auto",
  };

  const contactItems = (
    <div style={contactItemsStyle}>
      {/* Address */}
      <div style={itemBoxStyle}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
          <span style={{ fontSize: "1.5rem" }}>📍</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <span style={{ fontWeight: 600, color: textColor, display: "block" }}>{clinicName}</span>
            <span style={{ color: textColor, opacity: 0.8 }}>{address}</span>
          </div>
        </div>
      </div>

      {/* Phone */}
      {phone && (
        <div style={itemBoxStyle}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <span style={{ fontSize: "1.5rem" }}>📞</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <span style={{ fontWeight: 600, color: textColor, display: "block" }}>Phone</span>
              <a href={`tel:${phone.replace(/\s/g, "")}`} style={{ color: "#2563eb", textDecoration: "none" }}>
                {phone}
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Email */}
      {email && (
        <div style={itemBoxStyle}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <span style={{ fontSize: "1.5rem" }}>✉️</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <span style={{ fontWeight: 600, color: textColor, display: "block" }}>Email</span>
              <a href={`mailto:${email}`} style={{ color: "#2563eb", textDecoration: "none" }}>
                {email}
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Working Hours */}
      {workingHours && (
        <div style={itemBoxStyle}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
            <span style={{ fontSize: "1.5rem" }}>🕐</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <span style={{ fontWeight: 600, color: textColor, display: "block" }}>Working Hours</span>
              <span style={{ color: textColor, opacity: 0.8, whiteSpace: "pre-line", wordBreak: "break-word" }}>
                {workingHours}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Layout with map
  if (isWithMap && showMap && mapEmbedUrl) {
    return (
      <div style={containerStyle} className="contact-info">
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "32px" }}>
          <div>{contactItems}</div>
          <div style={{ borderRadius: "8px", overflow: "hidden" }}>
            <iframe
              src={mapEmbedUrl}
              title="Map"
              style={{ width: "100%", height: "100%", minHeight: "300px", border: 0 }}
              allowFullScreen
              loading="lazy"
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle} className="contact-info">
      {contactItems}
    </div>
  );
};
